import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Invite } from '@models/Core/Invite';
import { Participant } from '@models/Sessions/Participant';
import _ from 'lodash';
import * as io from 'socket.io-client';

import { Session } from '../../models/Sessions/Session';

export interface InviteSyncHandler {
  invite?: (i: Invite) => void;
  revoke?: (i: Invite) => void;
  update?: (i: Invite) => void;
  apply?: (i: Invite, p: Participant) => void;
}

@Injectable()
export class SessionSocketService {
  private socket: io.Socket;
  private listeners: any[];
  private inviteListeners: any[];

  connect(token: string) {
    if (token && !this.socket) {
      this.socket = io.connect(environment.backend + '/session', {
        path: '/socket.io-client',
        query: { token },
      });

      // this.sessionSocket.on('error', err => Raven.captureMessage(err));
      this.socket.on('error', err => console.error(err));
      this.listeners = [];
    }
  }

  disconnect() {
    if (this.socket && this.socket.connected) {
      this.socket.disconnect();
      this.socket = undefined;
    }
  }

  /**
   * Register listeners to sync an ideation session with updates on the model
   * Takes the ideation session we want to sync.
   * @param session
   */
  syncUpdates(session: Session) {
    const addListener = (event, callback) => {
      if (this.socket) {
        this.socket.on(event, update => {
          if (session._id === update._id) {
            callback(update);
          }
        });
      }

      //
      // TODO please fix this. an event is not a listener. either do proper naming,
      // or, as it would be the case with most conventional event listeners, hold a reference
      // to the listener.
      //
      this.listeners.push(event);
    };

    // Listen on all events

    // IdeationSession (This is only called on Import)
    addListener('session:update', update => {
      session.data.ideas = update.data.ideas;
      session.config.participants = update.config.participants;
      session.config.teams = update.config.teams;
      // $rootScope.$emit('redraw-matrix');
    });

    // Ideas (everything else is handled by the idea's own socket. These are handled here because they can create/delete an idea altogether)
    addListener('session-data-idea:create', update => {
      session.data.ideas.push(update.idea);
    });

    addListener('session-data-idea:publish', update => {
      if (update.idea.created_by === session.me._id) {
        (
          _.find(session.data.ideas, { _id: update.idea._id }) as any
        ).published = true;
      } else {
        session.data.ideas.push(update.idea);
      }
      // $rootScope.$emit('redraw-matrix');
    });

    addListener('session-data-idea:unpublish', update => {
      const index = _.findIndex(session.data.ideas, { _id: update.idea_id });
      if (session.data.ideas[index].created_by === session.me._id) {
        session.data.ideas[index].published = false;
      } else {
        session.data.ideas.splice(index, 1);
      }
      // $rootScope.$emit('redraw-matrix');
    });

    addListener('session-data-idea:delete', update => {
      _.remove(session.data.ideas, { _id: update.idea_id });
      // $rootScope.$emit('redraw-matrix');
    });

    addListener('session-data-idea:update', update => {
      const index = _.findIndex(session.data.ideas, { _id: update.idea._id });
      session.data.ideas[index] = update.idea;
    });

    // Announcement TODO play another sound
    addListener('session-data-announcement:create', update => {
      // TODO if the announcement modal is currently open, immediately sent a read-confirmation
      session.me.unreadAnnouncements++;
      // (Add a listener to the chat-modal-controller) -> Make Chat and Announcements different modals
      session.data.announcements.push(update.announcement);
    });

    addListener('session-data-announcement:read', () => {
      _.forEach(session.data.announcements, announcement => {
        announcement.read_by_me = true;
      });
      session.me.unreadAnnouncements = 0;
    });

    // Morphboxes
    addListener('session-data-morphbox:create', update => {
      session.data.morphboxes.push(update);
    });

    addListener('session-data-morphbox:delete', update => {
      _.remove(session.data.morphboxes, { _id: update.morphbox_id });
    });

    // Morphbox rows
    addListener('session-data-morphbox-row:create', update => {
      _.find(session.data.morphboxes, { _id: update.morphbox_id }).rows.push(
        update.row,
      );
    });

    addListener('session-data-morphbox-row:delete', update => {
      const morphbox = _.find(session.data.morphboxes, {
        _id: update.morphbox_id,
      });
      _.remove(morphbox.rows, { _id: update.row_id });
    });

    // Morphbox Elements
    addListener('session-data-morphbox-row-element:create', update => {
      const morphbox = _.find(session.data.morphboxes, {
        _id: update.morphbox_id,
      });
      (_.find(morphbox.rows, { _id: update.row_id }) as any).elements.push(
        update.element,
      );
    });

    addListener('session-data-morphbox-row-element:delete', update => {
      const morphbox = _.find(session.data.morphboxes, {
        _id: update.morphbox_id,
      });
      const row: any = _.find(morphbox.rows, { _id: update.row_id });
      _.remove(row.elements, { _id: update.element_id });
    });

    // Config
    addListener('session-config:update', update => {
      session.config = update.config;
      // (session.config.participants as any) = _.keyBy(session.config.participants, '_id');
      // (session.config.teams as any) = _.keyBy(session.config.teams, '_id');
    });

    // Notifications
    addListener('session-data-notifications:create', update => {
      session.data.notifications.push(update.notification);
    });

    addListener('session-data-notifications:read', update => {
      _.forEach(session.data.notifications, notification => {
        if (notification._id === update.notification_id) {
          notification.read_by_me = true;
        }
      });
    });

    addListener('session-config-participant:delete', update => {
      if (update.participant_id === session.me._id) {
        // TODO Handle gracefully.
      } else {
        _.remove(session.config.participants, { _id: update.participant._id });
      }
    });

    addListener('session-config-participant:create', update => {
      session.config.participants.push(update.participant);
    });

    addListener('session-config-participant:update', update => {
      session.config.participants.forEach(p => {
        if (p._id === update.participant._id) {
          p = update.participant;
        }
      });
    });

    addListener('session-config-team:delete', () => {
      // TODO
    });

    addListener('session-config-team:create', update => {
      session.config.teams[update.team._id] = update.team;
    });
  }

  /**
   * Removes listeners for updates on the sessionSocket
   */
  unsyncUpdates() {
    if (this.socket)
      _.forEach(this.listeners, event => this.socket.removeListener(event));
    this.listeners = [];
  }

  syncInvites(session, handler: InviteSyncHandler) {
    const addListener = (event, callback) => {
      if (this.socket) {
        this.socket.on(event, update => {
          if (session._id.toString() === update.session.toString()) {
            callback(update);
          }
        });
      }
    };

    if (handler.invite)
      addListener('session-invite:invite', update =>
        handler.invite(update.invite),
      );

    if (handler.revoke)
      addListener('session-invite:revoke', update =>
        handler.revoke(update.invite),
      );

    if (handler.update)
      addListener('session-invite:update', update =>
        handler.update(update.invite),
      );

    if (handler.apply)
      addListener('session-invite:apply', update =>
        handler.apply(update.invite, update.participant),
      );
  }

  unsyncInvites() {
    if (this.socket)
      _.forEach(this.inviteListeners, event =>
        this.socket.removeListener(event),
      );
    this.inviteListeners = [];
  }
}
